// =============================================================================
// Partner self-onboarding — web mirror of the partners mobile app flow:
//   passcode (peek_partner_passcode) → account (signUp) → onboarding
//   (finalize_partner_onboarding) → partner workspace session.
// =============================================================================

import { getSupabaseClient } from "@veorest/api-client";
import { toLoginEmail } from "./partnerAuth";

export interface PartnerPeek {
  name: string | null;
  email: string | null;
}

export type PartnerSignupErrorCode =
  | "passcode_invalid"
  | "passcode_expired"
  | "passcode_used"
  | "passcode_attempts"
  | "username_invalid"
  | "username_taken"
  | "email_invalid"
  | "email_taken"
  | "password_too_short"
  | "auth_failed"
  | "onboarding_failed";

export class PartnerSignupError extends Error {
  readonly code: PartnerSignupErrorCode;

  constructor(code: PartnerSignupErrorCode, message?: string) {
    super(message ?? code);
    this.name = "PartnerSignupError";
    this.code = code;
  }
}

/** Same rules as the partners mobile app (normalize_client_username + assert). */
const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{2,29}$/;
const RESERVED_USERNAMES = new Set([
  "admin",
  "oelox",
  "support",
  "owner",
  "root",
  "partner",
  "system",
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function mapPeekError(raw: string): PartnerSignupErrorCode {
  const msg = (raw || "").toLowerCase();
  if (msg.includes("passcode_expired")) return "passcode_expired";
  if (msg.includes("passcode_already_used")) return "passcode_used";
  if (msg.includes("passcode_attempts_exceeded")) return "passcode_attempts";
  return "passcode_invalid";
}

function mapSignUpError(raw: string): PartnerSignupErrorCode {
  const msg = (raw || "").toLowerCase();
  // Login id is the synthetic email derived from the username — a duplicate
  // here means the username is taken.
  if (msg.includes("already registered") || msg.includes("already exists")) {
    return "username_taken";
  }
  if (msg.includes("password")) return "password_too_short";
  if (msg.includes("email")) return "email_taken";
  return "onboarding_failed";
}

function mapFinalizeError(raw: string): PartnerSignupErrorCode {
  const msg = (raw || "").toLowerCase();
  if (msg.includes("passcode_expired")) return "passcode_expired";
  if (msg.includes("passcode_already_used")) return "passcode_used";
  if (msg.includes("passcode_attempts")) return "passcode_attempts";
  if (msg.includes("passcode")) return "passcode_invalid";
  if (msg.includes("username_taken")) return "username_taken";
  if (msg.includes("username_invalid") || msg.includes("username_required")) {
    return "username_invalid";
  }
  if (msg.includes("email") && msg.includes("taken")) return "email_taken";
  if (msg.includes("auth_required") || msg.includes("user_mismatch")) {
    return "auth_failed";
  }
  return "onboarding_failed";
}

/**
 * Peek a partner onboarding passcode (anon, rate-limited) → org info for the
 * onboarding form. Mirrors PartnerSignupRepository.peekPasscode (mobile).
 */
export async function peekPartnerPasscode(rawCode: string): Promise<PartnerPeek> {
  const supabase = getSupabaseClient();
  const code = rawCode.trim().toUpperCase();
  const { data, error } = await supabase.rpc("peek_partner_passcode", {
    p_code: code,
  });
  if (error) {
    throw new PartnerSignupError(mapPeekError(error.message), error.message);
  }
  const row = asRecord(data);
  const partner = asRecord(row?.partner ?? null) ?? row;
  return {
    name: partner?.name != null ? String(partner.name) : null,
    email: partner?.email != null ? String(partner.email) : null,
  };
}

export interface PartnerOnboardingInput {
  passcode: string;
  displayName: string;
  username: string;
  email: string;
  password: string;
}

/**
 * Create the auth account (username → synthetic login email) and finalize
 * partner onboarding. Leaves a live Supabase session on success.
 * Mirrors PartnerAuthNotifier.completeOnboarding (mobile).
 */
export async function completePartnerOnboarding(
  input: PartnerOnboardingInput
): Promise<void> {
  const code = input.passcode.trim().toUpperCase();
  const displayName = input.displayName.trim();
  const username = input.username.trim().toLowerCase();
  const email = input.email.trim().toLowerCase();

  if (!USERNAME_RE.test(username) || RESERVED_USERNAMES.has(username)) {
    throw new PartnerSignupError("username_invalid");
  }
  if (email && !email.includes("@")) {
    throw new PartnerSignupError("email_invalid");
  }
  if (input.password.length < 8) {
    throw new PartnerSignupError("password_too_short");
  }

  const supabase = getSupabaseClient();
  const loginEmail = toLoginEmail(username);

  const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
    email: loginEmail,
    password: input.password,
    options: {
      data: {
        display_name: displayName,
        onboarding: "partner",
        role: "partner",
      },
    },
  });
  if (signUpError) {
    throw new PartnerSignupError(mapSignUpError(signUpError.message), signUpError.message);
  }

  let user = signUpData.user;
  let session = signUpData.session;
  if (!session) {
    // Auto-confirm is expected for the synthetic domain; fall back to a plain
    // sign-in when the project requires confirmation.
    const { data: signInData, error: signInError } =
      await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: input.password,
      });
    if (signInError || !signInData.session) {
      throw new PartnerSignupError("auth_failed", signInError?.message);
    }
    user = signInData.user ?? user;
    session = signInData.session;
  }
  if (!user || !session) {
    throw new PartnerSignupError("auth_failed");
  }

  const { error: finalizeError } = await supabase.rpc(
    "finalize_partner_onboarding",
    {
      p_code: code,
      p_user_id: user.id,
      p_email: email || null,
      p_display_name: displayName,
      p_username: username,
    }
  );
  if (finalizeError) {
    throw new PartnerSignupError(
      mapFinalizeError(finalizeError.message),
      finalizeError.message
    );
  }
}
